import { Component, Injector, Input, OnInit } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { ActivatedRoute, Router } from '@angular/router';
import 'rxjs/add/operator/filter';
import { HookSystemService } from '@kaiser-software/hook-system';
import { Model } from '../../../api/model';
import { RouteService } from '../../../routing/services/route.service';
import { ToastService2 } from '../../../toast/services/toast2.service';
import { PluginDataInterface } from '../../interfaces/plugin-data.interface';
import { FollowService } from '../../../follows/services/follow.service';
import { FollowModel } from '../../../follows/models/follow.model';
import { trans } from '../../../localization/helpers';

@Component({
    selector: 'chassis-core-actions-plugin',
    templateUrl: './actions.plugin.html',
    styleUrls: ['./actions.plugin.scss'],
    providers: []
})
export class ActionsPlugin implements OnInit {

    /**
     * @type {boolean}
     */
    public loading = true;

    /**
     * @type {ActionsPluginDataInterface}
     */
    @Input() pluginData: PluginDataInterface;

    /**
     *
     */
    public data: ActionsPluginDataInterface;

    /**
     * @type {Observable<Model>}
     */
    protected modelSubject: Observable<Model>;

    /**
     * The prefix that is used for hooks
     *
     * @type {string}
     */
    @Input() hookPrefix: string;

    /**
     * Hooks indication string
     */
    protected hooksIndicator = 'actions-plugin';

    /**
     * Constructor
     *
     * @param {FollowService} followService
     * @param {HookSystemService} hookSystemService
     * @param {Injector} injector
     * @param {ActivatedRoute} route
     * @param {Router} router
     * @param {RouteService} routeService
     * @param {ToastService2} toastService
     */
    constructor(
        protected followService: FollowService,
        protected hookSystemService: HookSystemService,
        protected injector: Injector,
        protected route: ActivatedRoute,
        protected router: Router,
        protected routeService: RouteService,
        protected toastService: ToastService2) {
    }

    ngOnInit(): void {
        if (this.pluginData && this.pluginData.inputs) {
            if (this.pluginData.inputs.hasOwnProperty('modelSubject')) {
                this.modelSubject = this.pluginData.inputs['model$'];
            }

            if (this.pluginData.inputs.hasOwnProperty('actionsPluginData')) {
                this.data = this.pluginData.inputs['actionsPluginData'];

                this.applyHooks();

                if (this.data.followable && this.modelSubject) {
                    this.modelSubject.subscribe(model => {
                        if (model.exists() && model._service && model._service.restEndpoint) {
                            this.followService.defaultParams = {
                                entity: model._service.restEndpoint,
                                entityId: model.id
                            };

                            this.followService.checkFollowing().subscribe(result => {
                                if (result.following) {
                                    this.addUnfollowAction();
                                } else {
                                    this.addFollowAction();
                                }
                            }, error => {
                                console.log('CheckFollowing | Error: ', error);
                            });
                        }
                    }, error => {
                        console.log('ModelSubject subscribe | Error: ', error);
                    });
                }
            }
        }

        this.loading = false;
    }

    /**
     * Get the panel's dynamic classes.
     *
     * @return {{}}
     */
    public get panelClass() {
        return {
            'loading': this.loading,
            'panel-default': !this.loading,
        };
    }

    private applyHooks() {
        // Title
        this.data.title = this.hookSystemService.filter(this.hookPrefix + '-' + this.hooksIndicator + '-title', {
            title: this.data.title,
            modelSubject: this.modelSubject
        });
        if (this.data.title.hasOwnProperty('title')) {
            this.data.title = this.data.title['title'];
        }

        // Status
        this.data.status = this.hookSystemService.filter(this.hookPrefix + '-' + this.hooksIndicator + '-status', {
            status: this.data.status,
            modelSubject: this.modelSubject
        });
        if (this.data.status.hasOwnProperty('status')) {
            this.data.status = this.data.status['status'];
        }

        // Actions
        this.data.actions = this.hookSystemService.filter(this.hookPrefix + '-' + this.hooksIndicator + '-actions', {
            actions: this.data.actions,
            modelSubject: this.modelSubject
        });
        if (this.data.actions.hasOwnProperty('actions')) {
            this.data.actions = this.data.actions['actions'];
        }

        // Information
        this.data.information = this.hookSystemService.filter(this.hookPrefix + '-' + this.hooksIndicator + '-information', {
            information: this.data.information,
            modelSubject: this.modelSubject
        });
        if (this.data.information.hasOwnProperty('information')) {
            this.data.information = this.data.information['information'];
        }
    }

    /**
     * Add follow action
     */
    private addFollowAction() {
        this.removeUnfollowAction();

        const followAction: ActionsPluginActionInterface = {
            name: 'follow',
            icon: 'fal fa-rss',
            title: trans('Follow'),
            callback: () => {
                this.followService.follow().subscribe((follow: FollowModel) => {
                    this.addUnfollowAction();

                    this.toastService.success(trans('Follow success'));
                }, error => {
                    console.log('Follow | Error: ', error);
                });
            }
        };
        this.data.actions                                = this.data.actions || [];

        this.data.actions.push(followAction);
    }

    /**
     * Add unfollow action
     */
    private addUnfollowAction() {
        this.removeFollowAction();

        const unfollowAction: ActionsPluginActionInterface = {
            name: 'unfollow',
            icon: 'fal fa-rss',
            title: trans('Unfollow'),
            callback: () => {
                this.followService.unfollow().subscribe((follow: FollowModel) => {
                    this.addFollowAction();

                    this.toastService.success(trans('Unfollow success'));
                }, error => {
                    console.log('Unfollow | Error: ', error);
                });
            }
        };

        this.data.actions = this.data.actions || [];

        this.data.actions.push(unfollowAction);
    }

    /**
     * Remove the follow action
     */
    private removeFollowAction() {
        if (this.data.actions && Array.isArray(this.data.actions) && this.data.actions.length) {
            this.data.actions = this.data.actions.filter((action) => {
                return action.name !== 'follow';
            });
        }
    }

    /**
     * Remove the unfollow action
     */
    private removeUnfollowAction() {
        if (this.data.actions && Array.isArray(this.data.actions) && this.data.actions.length) {
            this.data.actions = this.data.actions.filter((action) => {
                return action.name !== 'unfollow';
            });
        }

    }
}

export interface ActionsPluginDataInterface {
    title: string,
    status?: ActionsPluginStatusInterface
    actions?: Array<ActionsPluginActionInterface>,
    information?: string
    followable?: boolean
}

export interface ActionsPluginActionInterface {
    name: string,
    icon?: string,
    title: string,
    callback: () => void
}

export interface ActionsPluginStatusInterface {
    label: string,
    title: string
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/plugins/components/actions/actions.plugin.ts